import type { AudioRef } from "../services/generated/client.js";
import { clipUrl } from "../services/http.js";

/**
 * Where a cue's audio actually comes from — phase 19b.
 *
 * Lives in `lib/` rather than beside either caller because **both** the phone
 * (`pages/party/`) and the ten-foot display (`pages/display/`) resolve a cue,
 * and `/display` may not import from the player shell — it ships as a packaged
 * TV app and that separation is what keeps it cheap (see `frontend.md`).
 *
 * # Why this is a function and not an inline ternary
 *
 * `AudioRef` is a discriminated union generated from the Rust enum, and a third
 * provider is a real possibility. Branching in one place means a new variant is
 * a `tsc` error here rather than silent nothing in two screens.
 */

/** What a screen should do with a cue. */
export type Playback =
  | {
      /** Play it through the `<audio>` element at this URL. */
      kind: "element";
      src: string;
    }
  | {
      /** Hand it to the Spotify Web Playback SDK on the sink. */
      kind: "spotify";
      /** The full URI the SDK wants, built here so the shape lives in one place. */
      uri: string;
    }
  | {
      /**
       * This build cannot play it.
       *
       * Not an error to throw: a screen that cannot play a card should sit
       * quietly rather than break the round, and the server should not have
       * dealt it in the first place (`AudioRef::is_playable_here`).
       */
      kind: "unsupported";
      provider: string;
    };

/**
 * Resolve a cue to something a screen can act on.
 *
 * ⚠️ The Spotify branch is **not wired to a player yet** — that is 19d, and
 * until it lands a Spotify card should never be dealt, because the deal
 * excludes what the installation cannot play. This returns the URI rather than
 * throwing so that the day it *is* wired, the change is at the call site and
 * not here.
 */
export function playbackFor(audio: AudioRef): Playback {
  switch (audio.provider) {
    case "local":
      return { kind: "element", src: clipUrl(audio.sha256) };
    case "spotify":
      return { kind: "spotify", uri: `spotify:track:${audio.track_id}` };
    default:
      // A provider this build has never heard of. `audio` is `never` here as
      // long as the union is exhaustive, so adding a variant to the Rust enum
      // and regenerating makes this line fail to compile.
      return {
        kind: "unsupported",
        provider: (audio as { provider: string }).provider,
      };
  }
}
